import { Integration } from '../types'
import { INTEGRATIONS } from '../data/integrations'
import { delay, refresh } from './store'
import { supabase } from './supabaseClient'

/** Proveedor del calendario global usado por todas las landings. */
export const GOOGLE_CALENDAR = 'google_calendar'

export function listIntegrations(): Integration[] {
  return INTEGRATIONS
}

/**
 * TODO(OAuth): aquí se lanzará el flujo real de conexión de cada integración.
 * Por ahora no se simula ninguna conexión activa.
 */
export async function requestConnection(integrationId: string): Promise<string> {
  await delay(null, 300)
  return integrationId
}

/** Lee la configuración guardada de una integración de la organización. */
export async function getIntegration(
  organizationId: string,
  provider: string,
): Promise<{ config: Record<string, unknown>; isActive: boolean } | null> {
  const { data, error } = await supabase
    .from('integrations')
    .select('config, is_active')
    .eq('organization_id', organizationId)
    .eq('provider', provider)
    .maybeSingle()
  if (error || !data) return null
  return {
    config: (data.config ?? {}) as Record<string, unknown>,
    isActive: data.is_active !== false,
  }
}

/**
 * Conecta el Google Calendar global de la empresa y lo propaga a todas las
 * landings que usan el calendario principal, para no configurarlo una por una.
 *
 * No cambia la lógica de reserva: solo actualiza la URL que abre el botón.
 */
export async function connectGoogleCalendar(
  organizationId: string,
  bookingUrl: string,
): Promise<void> {
  const existing = await getIntegration(organizationId, GOOGLE_CALENDAR)
  const row = {
    organization_id: organizationId,
    provider: GOOGLE_CALENDAR,
    config: { bookingUrl },
    is_active: Boolean(bookingUrl),
  }
  const { error } = existing
    ? await supabase
        .from('integrations')
        .update({ config: row.config, is_active: row.is_active })
        .eq('organization_id', organizationId)
        .eq('provider', GOOGLE_CALENDAR)
    : await supabase.from('integrations').insert(row)
  if (error) {
    throw new Error(`No se ha podido guardar la integración: ${error.message}`)
  }

  await applyGlobalCalendar(organizationId, bookingUrl)
  await refresh()
}

/** Escribe la URL global en los bloques de calendario que la usan. */
export async function applyGlobalCalendar(
  organizationId: string,
  bookingUrl: string,
): Promise<void> {
  const { data, error } = await supabase
    .from('landing_blocks')
    .select('id, content')
    .eq('organization_id', organizationId)
    .eq('type', 'calendario')
  if (error || !data?.length) return

  await Promise.all(
    data.map((block: { id: string; content: Record<string, unknown> | null }) => {
      const content = (block.content ?? {}) as Record<string, unknown>
      // Las landings con calendario propio no se tocan.
      if (content.calendarSource === 'custom') return Promise.resolve()
      return supabase
        .from('landing_blocks')
        .update({ content: { ...content, globalUrl: bookingUrl } })
        .eq('id', block.id)
    }),
  )
}
